import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { Modal, Tag, Tabs, Input, Button, message, DatePicker, List, Alert, Tooltip } from 'antd';
import FunnelSettingsTab from './FunnelSettingsTab.jsx';
import VacancyDetailsTab from './VacancyGeneralInfoTab.jsx';
import VacancyReportTab from './VacancyReportTab.jsx';
import CandidateList from '../Candidates/CandidateList.jsx';
import { createVacancyCandidate, vacancyCandidateReturnToWork } from '../../redux/vacancyCandidatesSlice';
import { fetchCandidateById } from '../../redux/candidatesSlice';
import { addVacancyFunnelStatus, updateVacancyFunnelStatus, updateVacancyStatus, updateVacancyStatusOptimistic, revertVacancyStatus } from '../../redux/vacanciesSlice';
import CreateModal from '../Candidates/CreateModal.jsx';
import CompletionModal from '../Candidates/CompletionModal.jsx';
import { useDispatch, useSelector } from 'react-redux';
import { columnLabels, STATUSES } from '../../constants/vacancyColumnLabels';
import StatusTag from '../StatusTag';
import { revertCandidateStatus } from '../../redux/candidatesSlice';
import { updateVacancyCandidateStatus, updateVacancyCandidateStatusOptimistic } from '../../redux/vacancyCandidatesSlice';
import { canCommentOnVacancy, canChangeVacancyStatus } from '../../utils/vacancyPermissions';
const { TabPane } = Tabs;
const { TextArea } = Input;

const CustomComment = ({ item, users }) => {
  const user = users?.find(u => u.id === item.created_by);
  const authorName = user ? `${user.first_name} ${user.last_name}` : 'Неизвестный пользователь';

  return (
    <div style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
      <div style={{ fontWeight: 'bold' }}>{authorName}</div>
      <div style={{ margin: '4px 0' }}>{item.comment}</div>
      <div style={{ fontSize: 12, color: '#999' }}>
        {new Date(item.created_at).toLocaleString()}
      </div>
    </div>
  );
};

const ViewModal = ({
  isOpen,
  onClose,
  vacancy,
  organisations,
  users,
  onCommentSubmit,
  onReminderSubmit,
  funnelStatuses,
  // data provided by parent (Details page)
  history,
  historyLoading,
  allVacancies,
  candidatesByVacancy,
  allCandidates,
  candidatesLoading,
  funnelConfig,
  funnelConfigLoading,
  funnelConfigError,
  orgId,
  refreshVacancyHistory,
  refreshVacancyCandidates,
  fetchFunnelConfig,
  refreshFunnelConfig,
}) => {
  const [comment, setComment] = useState('');
  const [reminder, setReminder] = useState(null);
  const [reminderText, setReminderText] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [isCreateCandidateModalOpen, setIsCreateCandidateModalOpen] = useState(false);
  const [createModalInitialValues, setCreateModalInitialValues] = useState(null);
  const [isAddCandidateModalOpen, setIsAddCandidateModalOpen] = useState(false);
  // funnelConfig and its loading/error are provided by parent via props
  const [returnToWorkModalVisible, setReturnToWorkModalVisible] = useState(false);
  const [returnToWorkComment, setReturnToWorkComment] = useState('');
  const [pendingReturnData, setPendingReturnData] = useState(null);
  const [localVacancy, setLocalVacancy] = useState(vacancy);
  const dispatch = useDispatch();
  const [funnelReadyOverride, setFunnelReadyOverride] = useState(false);
  const { role } = useSelector((state) => state.auth);
  const [completionModalVisible, setCompletionModalVisible] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);

  const isFunnelReady = useMemo(() => {
    // Если идет загрузка, считаем воронку не готовой
    if (funnelConfigLoading) {
      return false;
    }

    // Проверяем, что воронка настроена и сохранена
    // funnelConfig должен быть массивом с хотя бы одним элементом
    const hasFunnelConfig = Array.isArray(funnelConfig) && funnelConfig.length > 0;

    // Если есть override (например, после сохранения), считаем воронку готовой
    if (funnelReadyOverride) {
      return true;
    }

    // Воронка готова только если есть сохраненная конфигурация
    return hasFunnelConfig;
  }, [funnelReadyOverride, funnelConfig, funnelConfigLoading]);

  const getFiles = (candidate) => {
    return candidate?.files || [];
  };

  const fetchVacancyCandidates = useCallback(async () => {
    if (!vacancy?.id || !refreshVacancyCandidates) return;
    try {
      await refreshVacancyCandidates();
    } catch (error) {
      message.error('Ошибка при загрузке кандидатов');
    }
  }, [vacancy, refreshVacancyCandidates]);

  const addCandidateToVacancy = useCallback(async (candidateId) => {
    try {
      await dispatch(createVacancyCandidate({ vacancyId: vacancy.id, candidateId })).unwrap();
      fetchVacancyCandidates();
    } catch (error) {
      message.error('Ошибка при добавлении кандидата к вакансии');
      console.error(error);
    }
  }, [vacancy, dispatch, fetchVacancyCandidates]);

  // Parent drives fetching; here we only trigger helpers if needed via callbacks from UI actions

  useEffect(() => {
    setLocalVacancy(vacancy);
    // Сбрасываем override при смене вакансии
    setFunnelReadyOverride(false);
  }, [vacancy]);

  useEffect(() => {
    if (candidatesByVacancy && candidatesByVacancy.length > 0 && allCandidates && allCandidates.length > 0) {
      const candidate_ids = candidatesByVacancy.map(candidate => candidate.candidate_id);
      const filteredCandidates = allCandidates.filter(candidate =>
        candidate_ids.includes(candidate.id)
      );

      setCandidates(filteredCandidates);
    } else if (candidatesByVacancy && candidatesByVacancy.length === 0) {
      setCandidates([]);
    }
  }, [candidatesByVacancy, allCandidates]);

  // initial candidates are provided by parent

  const handleCreateCandidate = useCallback(() => {
    if (!localVacancy) return;

    setCreateModalInitialValues({
      vacancy_id: localVacancy.id,
      organisation_id: orgId
    });
    setIsCreateCandidateModalOpen(true);
  }, [localVacancy, orgId]);

  const handleCreateSuccess = useCallback(() => {
    fetchVacancyCandidates();
    message.success('Кандидат успешно создан и добавлен к вакансии');
  }, [fetchVacancyCandidates]);

  const handleCommentSubmit = useCallback(async () => {
    if (!canCommentOnVacancy(role)) {
      message.warning('У вас нет прав для добавления комментариев к вакансии');
      return;
    }

    if (!comment.trim()) {
      message.warning('Введите комментарий перед отправкой');
      return;
    }
    try {
      if (onCommentSubmit) {
        await onCommentSubmit(vacancy.id, comment);
      }
      setComment('');
      if (refreshVacancyHistory) {
        await refreshVacancyHistory();
      }
    } catch (error) {
      message.error('Ошибка при отправке комментария');
      console.error(error);
    }
  }, [comment, vacancy, onCommentSubmit, refreshVacancyHistory, role]);

  const getFieldDisplayName = useCallback((fieldName) => {
    return columnLabels[fieldName] || fieldName;
  }, []);

  const comments = useMemo(() => {
    if (!Array.isArray(history?.comments)) return [];
    return history?.comments?.filter(item => item.comment);
  }, [history]);

  const historyItems = useMemo(() => {
    if (!history || typeof history !== 'object' || !localVacancy) return [];

    const items = [];

    const isEmptyArrayValue = (val) => {
      if (Array.isArray(val)) {
        return val.length === 0;
      }
      if (typeof val === 'string') {
        const trimmed = val.trim();
        if (trimmed === '[]') return true;
        if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
          try {
            const parsed = JSON.parse(trimmed);
            return Array.isArray(parsed) && parsed.length === 0;
          } catch (_) {
            return false;
          }
        }
      }
      return false;
    };

    Object.entries(history).forEach(([fieldName, fieldHistory]) => {
      if (fieldName === 'comments' || !Array.isArray(fieldHistory)) return;

      const sortedHistory = [...fieldHistory].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

      sortedHistory.forEach((item, index) => {
        const originalFieldName = fieldName;
        const displayFieldName = getFieldDisplayName(fieldName);

        if (item.previous_value === false || item.previous_value === 'false') {
          return;
        }

        const formatValue = (val) => {
          if (fieldName === 'assignee_id' && val) {
            const user = users?.find(u => u.id === val || u.id === parseInt(val));
            return user ? `${user.first_name} ${user.last_name}` : `ID: ${val}`;
          }

          if (typeof val === 'object' && val !== null) {
            if (val.language && val.level) {
              return `${val.language.trim()} - ${val.level}`;
            }
            if (val.program && val.level) {
              return `${val.program.trim()} - ${val.level}`;
            }
            if (Array.isArray(val) && val.length > 0) {
              if (val[0].language && val[0].level) {
                return val.map(item => `${item.language.trim()} - ${item.level}`).join(', ');
              }
              if (val[0].program && val[0].level) {
                return val.map(item => `${item.program.trim()} - ${item.level}`).join(', ');
              }
            }
            return JSON.stringify(val);
          }
          if (typeof val === 'string' && val.startsWith('[') && val.includes('language')) {
            try {
              const parsed = JSON.parse(val);
              if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].language && parsed[0].level) {
                return parsed.map(item => `${item.language.trim()} - ${item.level}`).join(', ');
              }
            } catch (e) {
              const languageMatch = val.match(/language':\s*'([^']+)',\s*'level':\s*'([^']+)'/g);
              if (languageMatch) {
                const languages = languageMatch.map(match => {
                  const langMatch = match.match(/language':\s*'([^']+)',\s*'level':\s*'([^']+)'/);
                  if (langMatch) {
                    return `${langMatch[1].trim()} - ${langMatch[2]}`;
                  }
                  return null;
                }).filter(Boolean);
                if (languages.length > 0) {
                  return languages.join(', ');
                }
              }
            }
          }
          if (typeof val === 'string' && val.startsWith('[') && val.includes('program')) {
            try {
              const parsed = JSON.parse(val);
              if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].program && parsed[0].level) {
                return parsed.map(item => `${item.program.trim()} - ${item.level}`).join(', ');
              }
            } catch (e) {
              const programMatch = val.match(/program':\s*'([^']+)',\s*'level':\s*'([^']+)'/g);
              if (programMatch) {
                const programs = programMatch.map(match => {
                  const progMatch = match.match(/program':\s*'([^']+)',\s*'level':\s*'([^']+)'/);
                  if (progMatch) {
                    return `${progMatch[1].trim()} - ${progMatch[2]}`;
                  }
                  return null;
                }).filter(Boolean);
                if (programs.length > 0) {
                  return programs.join(', ');
                }
              }
            }
          }
          return val;
        };

        let currentValue;
        if (index === sortedHistory.length - 1) {
          currentValue = localVacancy[fieldName];
        } else {
          currentValue = sortedHistory[index + 1].previous_value;
        }

        if (
          (originalFieldName === 'language_skills' ||
            originalFieldName === 'software_skills' ||
            originalFieldName === 'language_knowledge' ||
            originalFieldName === 'software_knowledge') &&
          (isEmptyArrayValue(item.previous_value) || isEmptyArrayValue(currentValue))
        ) {
          return; // skip entries with empty arrays for these fields
        }

        items.push({
          ...item,
          field_name: displayFieldName,
          original_field_name: originalFieldName,
          previous_value: formatValue(item.previous_value === 'None' ? null : item.previous_value),
          current_value: formatValue(currentValue),
          is_initial_setup: item.previous_value === 'None'
        });
      });
    });

    return items.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [history, getFieldDisplayName, localVacancy, users]);

  const renderHistoryItem = useCallback((item) => {
    const user = users?.find(u => u.id === item.created_by);
    const authorName = user ? `${user.first_name} ${user.last_name}` : 'Неизвестный пользователь';

    const renderValue = (value, fieldName) => {
      if (fieldName === 'Рекрутер' && value) {
        const user = users?.find(u => u.id === value || u.id === parseInt(value));
        return user ? `${user.first_name} ${user.last_name}` : `${value}`;
      }

      if (typeof value === 'string' && value.startsWith('[') && value.includes('language')) {
        try {
          const parsed = JSON.parse(value);
          if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].language && parsed[0].level) {
            return parsed.map(item => `${item.language.trim()} - ${item.level}`).join(', ');
          }
        } catch (e) {
          const languageMatch = value.match(/language':\s*'([^']+)',\s*'level':\s*'([^']+)'/g);
          if (languageMatch) {
            const languages = languageMatch.map(match => {
              const langMatch = match.match(/language':\s*'([^']+)',\s*'level':\s*'([^']+)'/);
              if (langMatch) {
                return `${langMatch[1].trim()} - ${langMatch[2]}`;
              }
              return null;
            }).filter(Boolean);
            if (languages.length > 0) {
              return languages.join(', ');
            }
          }
        }
      }
      if (typeof value === 'string' && value.startsWith('[') && value.includes('program')) {
        try {
          const parsed = JSON.parse(value);
          if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].program && parsed[0].level) {
            return parsed.map(item => `${item.program.trim()} - ${item.level}`).join(', ');
          }
        } catch (e) {
          const programMatch = value.match(/program':\s*'([^']+)',\s*'level':\s*'([^']+)'/g);
          if (programMatch) {
            const programs = programMatch.map(match => {
              const progMatch = match.match(/program':\s*'([^']+)',\s*'level':\s*'([^']+)'/);
              if (progMatch) {
                return `${progMatch[1].trim()} - ${progMatch[2]}`;
              }
              return null;
            }).filter(Boolean);
            if (programs.length > 0) {
              return programs.join(', ');
            }
          }
        }
      }

      if (typeof value === 'object' && value !== null) {
        if (value.language && value.level) {
          return `${value.language.trim()} - ${value.level}`;
        }
        if (value.program && value.level) {
          return `${value.program.trim()} - ${value.level}`;
        }
        if (Array.isArray(value) && value.length > 0) {
          if (value[0].language && value[0].level) {
            return value.map(item => `${item.language.trim()} - ${item.level}`).join(', ');
          }
          if (value[0].program && value[0].level) {
            return value.map(item => `${item.program.trim()} - ${item.level}`).join(', ');
          }
        }
        return JSON.stringify(value);
      }

      if (fieldName === columnLabels.status && STATUSES[value]) {
        return <StatusTag status={value} statuses={STATUSES} />;
      }

      if (value === 'true' || value === true) {
        return <Tag color="green">Да</Tag>;
      }
      if (value === 'false' || value === false) {
        return null;
      }

      return value || 'Не указано';
    };

    return (
      <div style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
        <div>
          <strong>{authorName}</strong> изменил поле: {item.field_name}
        </div>
        <div style={{ marginTop: 4 }}>
          {item.is_initial_setup ? (
            <span style={{ color: '#999' }}>Установлено значение: </span>
          ) : (
            <>
              <span style={{ color: '#999' }}>Было: </span>
              {renderValue(item.previous_value, item.field_name)}
              <span style={{ margin: '0 8px' }}>→</span>
            </>
          )}
          <span style={{ fontWeight: 'normal' }}>{renderValue(item.current_value, item.field_name)}</span>
        </div>
        {item.comment && (
          <div style={{ marginTop: 4, fontStyle: 'italic', color: '#666' }}>
            Комментарий: {item.comment}
          </div>
        )}
        <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
          {new Date(item.created_at).toLocaleString()}
        </div>
      </div>
    );
  }, [users]);

  const handleReminderSubmit = useCallback(() => {
    if (!reminder) {
      message.warning('Выберите дату и время для напоминания');
      return;
    }
    if (!reminderText.trim()) {
      message.warning('Введите текст напоминания');
      return;
    }
    if (onReminderSubmit) {
      onReminderSubmit(vacancy.id, reminder, reminderText);
    }
    setReminder(null);
    setReminderText('');
  }, [reminder, reminderText, vacancy, onReminderSubmit]);

  const getStatusKeyFromName = useCallback((statusName) => {
    if (statusName.includes('Новый')) return 'new';
    if (statusName.includes('Перезвонить')) return 'call_back';
    if (statusName.includes('Собеседование')) return 'interview';
    if (statusName.includes('Сбор рекомендаций')) return 'references';
    if (statusName.includes('Предложение о работе')) return 'offer';
    if (statusName.includes('Оформление документов')) return 'documentation';
    if (statusName.includes('Трудоустроен') || statusName.includes('Завершить')) return 'hired';
    if (statusName.includes('Отклонён') || statusName.includes('Отказ')) return 'failed';
    return null;
  }, []);

  const isCompletionStatus = useCallback((statusId) => {
    if (!funnelStatuses || funnelStatuses.length === 0) return false;

    const status = funnelStatuses.find(s =>
      (s.id === statusId || s.status_id === statusId)
    );

    if (!status) return false;

    const statusName = status.name || status.status_name || '';
    const statusKey = getStatusKeyFromName(statusName);

    return statusKey === 'hired';
  }, [funnelStatuses, getStatusKeyFromName]);

  const handleStatusChange = async (item, newStatus, newIndex) => {
    let candidateId, vacancyId, originalStatus;

    if (item.candidate && item.vacancyInfo) {
      candidateId = item.candidate.id;
      vacancyId = item.vacancyInfo.vacancy_id || item.vacancyInfo.id;
      originalStatus = item.candidate.status;
    } else {
      candidateId = item.id;
      vacancyId = item.vacancy_id || localVacancy?.id || 1;
      originalStatus = item.status;
    }

    // Проверяем, является ли новый статус статусом завершения
    if (isCompletionStatus(parseInt(newStatus))) {
      setPendingStatusChange({
        candidateId,
        vacancyId,
        originalStatus,
        newStatus: parseInt(newStatus)
      });
      setCompletionModalVisible(true);
      return;
    }

    // Если это не статус завершения, обновляем статус напрямую
    try {
      await dispatch(updateVacancyCandidateStatus({
        vacancyId: vacancyId,
        candidateId: candidateId,
        statusId: parseInt(newStatus)
      })).unwrap();
      await dispatch(fetchCandidateById({ id: candidateId, organisationId: orgId })).unwrap();

      dispatch(updateVacancyCandidateStatusOptimistic({ vacancyId: vacancyId, candidateId: candidateId, statusId: parseInt(newStatus) }));
      await fetchVacancyCandidates();
    } catch (error) {
      console.error('Error updating candidate status:', error);
      dispatch(revertCandidateStatus({ id: candidateId, originalStatus }));
      message.error('Ошибка при обновлении статуса кандидата');
    }
  };

  const handleCompletionConfirm = useCallback(async (completionData) => {
    if (!pendingStatusChange) return;

    const { candidateId, vacancyId, originalStatus, newStatus } = pendingStatusChange;

    try {
      dispatch(updateVacancyCandidateStatusOptimistic({
        vacancyId: vacancyId,
        candidateId: candidateId,
        statusId: newStatus
      }));

      await dispatch(updateVacancyCandidateStatus({
        vacancyId: vacancyId,
        candidateId: candidateId,
        statusId: newStatus,
        completionData: completionData
      })).unwrap();

      await dispatch(fetchCandidateById({ id: candidateId, organisationId: orgId })).unwrap();
      await fetchVacancyCandidates();

      setCompletionModalVisible(false);
      setPendingStatusChange(null);
      message.success('Статус кандидата обновлен');
    } catch (error) {
      console.error('Error updating candidate status:', error);
      dispatch(revertCandidateStatus({
        vacancyId: vacancyId,
        candidateId: candidateId,
        originalStatusId: originalStatus
      }));
      message.error('Ошибка при обновлении статуса кандидата');
      setCompletionModalVisible(false);
      setPendingStatusChange(null);
    }
  }, [pendingStatusChange, dispatch, orgId, fetchVacancyCandidates]);

  const handleCompletionCancel = useCallback(() => {
    setCompletionModalVisible(false);
    setPendingStatusChange(null);
  }, []);

  const handleAddVacancyFunnelStatus = useCallback(async (funnelStatus) => {
    try {
      await dispatch(addVacancyFunnelStatus({ id: vacancy.id, funnelStatus })).unwrap();
      setFunnelReadyOverride(true);
      fetchFunnelConfig();
    } catch (error) {
      message.error('Ошибка при сохранении конфигурации воронки');
    }
  }, [vacancy, dispatch, fetchFunnelConfig]);

  const handleUpdateVacancyFunnelStatus = useCallback(async (funnelStatus) => {
    try {
      await dispatch(updateVacancyFunnelStatus({ id: vacancy.id, funnelStatus })).unwrap();
      setFunnelReadyOverride(true);
      fetchFunnelConfig();
    } catch (error) {
      message.error('Ошибка при обновлении конфигурации воронки');
    }
  }, [vacancy, dispatch, fetchFunnelConfig]);

  const handleReturnToWork = useCallback((candidateId) => {
    setPendingReturnData({ vacancyId: vacancy.id, candidateId });
    setReturnToWorkModalVisible(true);
  }, [vacancy]);

  const handleReturnToWorkConfirm = async () => {
    if (!returnToWorkComment.trim()) {
      message.error('Комментарий обязателен для возврата в работу');
      return;
    }

    try {
      await dispatch(vacancyCandidateReturnToWork({
        vacancyId: pendingReturnData.vacancyId,
        candidateId: pendingReturnData.candidateId,
        comment: returnToWorkComment
      })).unwrap();

      message.success('Кандидат успешно возвращен в работу');

      // Обновляем данные кандидата
      await dispatch(fetchCandidateById({ id: pendingReturnData.candidateId, organisationId: orgId })).unwrap();

      // Обновляем список кандидатов вакансии
      await fetchVacancyCandidates();

      // Закрываем модальное окно и сбрасываем состояние
      setReturnToWorkModalVisible(false);
      setReturnToWorkComment('');
      setPendingReturnData(null);
    } catch (error) {
      message.error('Ошибка при возврате кандидата в работу');
      console.error(error);
    }
  };

  const handleReturnToWorkCancel = () => {
    setReturnToWorkModalVisible(false);
    setReturnToWorkComment('');
    setPendingReturnData(null);
  };

  const handleTakeInWork = useCallback(async () => {
    if (!canChangeVacancyStatus(role)) {
      message.warning('У вас нет прав для изменения статуса вакансии');
      return;
    }

    if (!localVacancy || localVacancy.status !== 'new' || localVacancy.is_template) return;
    if (!isFunnelReady) {
      message.warning('Сначала настройте воронку на вкладке "Настройка воронки"');
      return;
    }
    const originalStatus = localVacancy.status;
    try {
      dispatch(updateVacancyStatusOptimistic({ id: localVacancy.id, status: 'in_progress' }));
      await dispatch(updateVacancyStatus({ id: localVacancy.id, organisationId: orgId, status: 'in_progress' })).unwrap();
      setLocalVacancy(prev => ({ ...prev, status: 'in_progress' }));
      message.success('Вакансия взята в работу');
      if (refreshVacancyHistory) {
        await refreshVacancyHistory();
      }
      if (fetchFunnelConfig) {
        await fetchFunnelConfig();
      }
    } catch (error) {
      dispatch(revertVacancyStatus({ id: localVacancy.id, originalStatus }));
      message.error('Не удалось взять вакансию в работу');
    }
  }, [dispatch, localVacancy, orgId, refreshVacancyHistory, fetchFunnelConfig, isFunnelReady, role]);


  return (
    <Modal
      title="Просмотр вакансии"
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={1000}
      destroyOnClose
    >
      {localVacancy && (
        <Tabs
          defaultActiveKey="1"
          tabBarExtraContent={
            (localVacancy?.status === 'new' && !localVacancy?.is_template) ? (
              <Tooltip title={
                !canChangeVacancyStatus(role) ? 'У вас нет прав для изменения статуса вакансии' :
                  !isFunnelReady ? 'Настройте воронку, затем вакансию можно будет взять в работу' :
                    null
              }>
                <span>
                  <Button
                    type="primary"
                    onClick={handleTakeInWork}
                    disabled={!isFunnelReady || !canChangeVacancyStatus(role)}
                  >
                    Взять в работу
                  </Button>
                </span>
              </Tooltip>
            ) : null
          }
        >
          <TabPane tab="Общая информация" key="1">
            <VacancyDetailsTab
              vacancy={localVacancy}
              organisations={organisations}
              users={users}
            />
          </TabPane>

          {!localVacancy.is_template && (
            <TabPane tab="Кандидаты" key="2">
              {
                !isFunnelReady ? (
                  <Alert
                    message="Воронка не настроена"
                    description="Для работы с кандидатами необходимо сначала настроить воронку в разделе 'Настройка воронки'"
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                ) : null
              }
              <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end', gap: 16 }}>
                <Button
                  onClick={() => setIsAddCandidateModalOpen(true)}
                  disabled={!isFunnelReady}
                >
                  Добавить кандидата
                </Button>
                <Button
                  type="primary"
                  onClick={handleCreateCandidate}
                  disabled={!isFunnelReady}
                >
                  Создать кандидата
                </Button>
              </div>

              {candidates.length > 0 ? (
                <CandidateList
                  candidates={candidates}
                  vacancies={[localVacancy]}
                  getFiles={getFiles}
                  loadVacancyData={false}
                  candidateVacancyData={candidatesByVacancy || []}
                  visibleColumns={['id', 'full_name', 'phone_number', 'vacancy_and_status', 'files', 'completion_work', 'failure_reason', 'actions']}
                  isVacancyContext={true}
                  currentVacancy={localVacancy}
                  funnelConfig={funnelConfig}
                  onStatusChange={handleStatusChange}
                  onReturnToWork={handleReturnToWork}
                  isModalMode={true}
                />
              ) : (
                <p>Нет кандидатов</p>
              )
              }

              <CreateModal
                isOpen={isCreateCandidateModalOpen}
                onCancel={() => setIsCreateCandidateModalOpen(false)}
                onSuccess={handleCreateSuccess}
                initialValues={createModalInitialValues}
                vacancies={[vacancy]}
              />

              <Modal
                title="Добавить кандидата"
                open={isAddCandidateModalOpen}
                onCancel={() => setIsAddCandidateModalOpen(false)}
                footer={null}
                width={1000}
                destroyOnClose
              >
                {candidatesLoading ? (
                  <p>Загрузка кандидатов...</p>
                ) : (
                  <CandidateList
                    candidates={allCandidates.filter(c =>
                      !candidates.some(vc => vc.id === c.id)
                    )}
                    vacancies={allVacancies}
                    onView={(candidate) => {
                      addCandidateToVacancy(candidate.id);
                      setIsAddCandidateModalOpen(false);
                    }}
                    visibleColumns={['full_name', 'phone_number', 'vacancy_and_status', 'actions']}
                    isSelectMode={true}
                    onSelect={addCandidateToVacancy}
                    loadVacancyData={false}
                    candidateVacancyData={null}
                  />
                )}
              </Modal>
            </TabPane>
          )}

          {!localVacancy.is_template && (
            <TabPane tab="Настройка воронки" key="3">
              {funnelConfigLoading ? (
                <Alert message="Загрузка настроек воронки..." type="info" />
              ) : funnelConfigError ? (
                <Alert message={funnelConfigError} type="error" />
              ) : (
                <FunnelSettingsTab
                  funnelStatuses={funnelStatuses}
                  addVacancyFunnelStatus={handleAddVacancyFunnelStatus}
                  updateVacancyFunnelStatus={handleUpdateVacancyFunnelStatus}
                  currentConfig={funnelConfig}
                  vacancyId={localVacancy?.id}
                  vacancyStatus={localVacancy?.status}
                />
              )}
            </TabPane>
          )}

          {!localVacancy.is_template && (
            <TabPane tab="История" key="4" loading={historyLoading}>
              {historyItems.length > 0 ? (
                <List dataSource={historyItems} renderItem={renderHistoryItem} />
              ) : (
                <p>История изменений отсутствует</p>
              )}
            </TabPane>
          )}

          <TabPane tab="Комментарии" key="5" loading={historyLoading}>
            {comments && comments.length > 0 ? (
              <List
                className="comment-list"
                header={`${comments.length} комментария(ев)`}
                itemLayout="horizontal"
                dataSource={comments}
                renderItem={item => <CustomComment item={item} users={users} />}
              />
            ) : (
              <p>Нет комментариев</p>
            )}
          </TabPane>

          {role === 'superadmin' && !localVacancy.is_template && (
            <TabPane tab="Отчетность" key="6">
              <VacancyReportTab
                vacancy={localVacancy}
                organisations={organisations}
              />
            </TabPane>
          )}
        </Tabs >
      )}

      {canCommentOnVacancy(role) && (
        <div style={{ marginTop: 24 }}>
          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 8 }}>
            Ваш комментарий
          </label>
          <TextArea
            rows={4}
            value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder="Оставьте комментарий..."
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
            <Tooltip title="Отправить комментарий">
              <Button
                type="primary"
                onClick={handleCommentSubmit}
                disabled={!canCommentOnVacancy(role)}
              >
                Отправить комментарий
              </Button>
            </Tooltip>
          </div>

          <div style={{ marginTop: 32 }}>
            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 8 }}>
              Напоминание
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <DatePicker
                showTime
                value={reminder}
                onChange={setReminder}
                placeholder="Выберите дату и время"
                style={{ width: '100%' }}
                inputReadOnly={true}
              />
              <TextArea
                rows={3}
                value={reminderText}
                onChange={e => setReminderText(e.target.value)}
                placeholder="О чем напомнить..."
                style={{ width: '100%' }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="default"
                  onClick={handleReminderSubmit}
                >
                  Напомнить
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal
        title="Возврат кандидата в работу"
        open={returnToWorkModalVisible}
        onOk={handleReturnToWorkConfirm}
        onCancel={handleReturnToWorkCancel}
        okText="Вернуть в работу"
        cancelText="Отмена"
        okButtonProps={{ disabled: !returnToWorkComment.trim() }}
      >
        <div style={{ marginBottom: 16 }}>
          <p>Для возврата кандидата в работу необходимо указать комментарий:</p>
          <TextArea
            rows={4}
            placeholder="Введите комментарий (обязательно)"
            value={returnToWorkComment}
            onChange={(e) => setReturnToWorkComment(e.target.value)}
            required
          />
        </div>
      </Modal>

      <CompletionModal
        visible={completionModalVisible}
        onCancel={handleCompletionCancel}
        onConfirm={handleCompletionConfirm}
        loading={false}
      />
    </Modal >
  );
};

export default memo(ViewModal);